<script setup lang="ts">
import type { Project } from '~/utils/project/Project'
import type { AddonOpportunity, InstallmentsWithBenefits, ParsedInstallment, SimulationOpportunities } from '~/utils/simulation/Simulation'
import type { InstallmentResponse } from '~/utils/simulation/SimulationApi'
import IconOpenInNew from '~icons/material-symbols/open-in-new'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import InstallmentCard from './InstallmentCard.vue'

const props = defineProps<{
  project: Project
  loadingDetails: boolean
  disableFields: boolean
  installmentList: InstallmentResponse | null
  refetchInstallment: () => Promise<any[] | void>
  setSelected: (value: any, is_user_action?: boolean) => void
  hasDefaultInstallment: boolean
  isProjectDisabled?: boolean
  showEmptyStateNotInstallments: boolean
  preSelectedInstallment?: 'with_combo' | 'with_combo_comission' | 'with_combo_conecte' | 'without_combo' | 'unselected'
  loadingAddons: boolean
  financingValue: number
  personType: string
  selectedInstallments?: number
}>()

const emit = defineEmits<{
  openComboConnectModal: ['benefits' | 'inverters']
  openSuppliersModal: []
}>()

const enableSelectInstallmentCombo = ref(false)

const { formatMoney } = useMoney()

const { getSimulationOpportunities } = useResumeStore()

// Feature flags
const { data: enableSelectInstallmentComboData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ONLY_INSTALLMENT_COMBO, false)
const { data: showComboCommissionInstallmentData } = useFeatureFlag<boolean>(FEATURE_FLAGS.COMMI_COMBO_INSTALLMENT, false)
const { data: comboConnectEnabledData } = useFeatureFlag<boolean>(FEATURE_FLAGS.COMBO_CONNECT_ENABLED_FOR_INTEGRATOR, false, true)
const { data: comboRegionalOfferData } = useFeatureFlag<boolean>(FEATURE_FLAGS.COMBO_REGIONAL_OFFER, false)

const enableSelectInstallmentComboValue = computed(() => enableSelectInstallmentComboData.value || false)
const showComboCommissionInstallmentValue = computed(() => showComboCommissionInstallmentData.value || false)
const comboConnectEnabledValue = computed(() => comboConnectEnabledData.value || props.project.selected_installments?.is_combo_connect || false)
const comboRegionalOfferValue = computed(() => comboRegionalOfferData.value || false)
const isComboConnectOnly = computed(() => props.project?.combo_conecte_only || false)

function installmentListAdapter(installments: InstallmentResponse | null): InstallmentsWithBenefits | null {
  if (!installments)
    return null

  const comboAddonOriginal = installments.original_simulation?.addons?.find((addon) => {
    return addon.type === 'combo'
  })

  const comboAddonSuggested = installments.suggested_simulation?.addons?.find((addon) => {
    return addon.type === 'combo'
  })

  const comboConnectOriginal = installments.original_simulation?.addons?.find((addon) => {
    return addon.type === 'combo_connect' || addon.product_slug === 'Combo Connect'
  })

  const hasProposal = !!installments.original_simulation?.proposal

  if (!hasProposal && !comboAddonOriginal && !comboAddonSuggested && !comboConnectOriginal)
    return null

  const isProposalDisabled = installments.original_simulation?.proposal?.disabled

  let mappedProposal: ParsedInstallment | null = null
  let mappedProposalWithComboAddon: ParsedInstallment | null = null

  if (hasProposal) {
    mappedProposal = {
      ...installments.original_simulation.proposal.proposal,
      ...installments.original_simulation.proposal.parameters,
      simulation_id: installments.original_simulation.id,
      engine_simulation_id: installments.original_simulation.proposal.engine_simulation_id || '0',
      id: installments.original_simulation.id,
      combo: false,
      is_from_suggested: false,
      difference: 0,
      reducedInstallmentPrice: 0,
    }

    const installmentWithComboPositiveDifferenceOriginal = comboAddonOriginal?.installment_price ? -comboAddonOriginal.installment_price : 0
    const installmentWithComboReducedInstallmentPrice = -(comboAddonOriginal?.reduced_installment_price ?? comboAddonOriginal?.installment_price ?? 0)

    mappedProposalWithComboAddon = {
      ...installments.original_simulation.proposal.proposal,
      ...installments.original_simulation.proposal.parameters,
      simulation_id: comboAddonOriginal?.simulation_id || '0',
      engine_simulation_id: comboAddonOriginal?.engine_simulation_id || '0',
      id: installments.original_simulation?.addons?.find((addon) => {
        return addon.type === 'combo'
      })?.id || '0',
      combo: true,
      difference: installmentWithComboPositiveDifferenceOriginal,
      reducedInstallmentPrice: installmentWithComboReducedInstallmentPrice,
      installments_value: (installments.original_simulation.proposal.proposal.installments_value - installmentWithComboPositiveDifferenceOriginal),
      monthly_interest_rate: comboAddonOriginal?.monthly_interest_rate || 0,
      cet: comboAddonOriginal?.cet || 0,
      is_from_suggested: false,
    }
  }

  const installmentWithComboPositiveDifferenceSugggest = comboAddonSuggested?.installment_price ? -comboAddonSuggested?.installment_price : 0
  const installmentWithComboReducedInstallmentPriceSuggest = -(comboAddonSuggested?.reduced_installment_price ?? comboAddonSuggested?.installment_price ?? 0)

  const mappedProposalWithComboComissionAddon: ParsedInstallment | null = installments.suggested_simulation?.proposal
    ? {
        ...installments.suggested_simulation.proposal.proposal,
        ...installments.suggested_simulation.proposal.parameters,
        simulation_id: installments.suggested_simulation.id,
        engine_simulation_id: comboAddonSuggested?.engine_simulation_id || '0',
        id: installments.suggested_simulation.id,
        combo: true,
        difference: installmentWithComboPositiveDifferenceSugggest,
        reducedInstallmentPrice: installmentWithComboReducedInstallmentPriceSuggest,
        installments_value: (installments.suggested_simulation.proposal.proposal.installments_value - installmentWithComboPositiveDifferenceSugggest),
        monthly_interest_rate: comboAddonSuggested?.monthly_interest_rate || 0,
        cet: comboAddonSuggested?.cet || 0,
        is_from_suggested: true,
      }
    : null

  const installmentWithComboReducedInstallmentPriceConnect = -(comboConnectOriginal?.reduced_installment_price ?? comboConnectOriginal?.installment_price ?? 0)

  let mappedProposalWithComboConecte: ParsedInstallment | null = null

  if (comboConnectOriginal) {
    if (installments.original_simulation?.proposal) {
      mappedProposalWithComboConecte = {
        ...installments.original_simulation.proposal.proposal,
        ...installments.original_simulation.proposal.parameters,
        simulation_id: comboConnectOriginal.simulation_id,
        engine_simulation_id: comboConnectOriginal.engine_simulation_id,
        id: comboConnectOriginal.id,
        combo: true,
        combo_conecte: true,
        combo_conecte_only: isComboConnectOnly.value,
        difference: -comboConnectOriginal.installment_price,
        reducedInstallmentPrice: installmentWithComboReducedInstallmentPriceConnect,
        installments_value: (installments.original_simulation.proposal.proposal.installments_value + comboConnectOriginal.installment_price),
        monthly_interest_rate: comboConnectOriginal.monthly_interest_rate || 0,
        cet: comboConnectOriginal.cet || 0,
        is_from_suggested: false,
      }
    }
    else {
      const projectValue = props.project?.project_value || 0
      const projectDownPayment = props.project?.down_payment || 0
      const selectedInstallmentsValue = props.selectedInstallments
        || props.project?.installments_conditions?.suggested_installments
        || props.project?.max_installments
        || 0

      mappedProposalWithComboConecte = {
        simulation_id: comboConnectOriginal.simulation_id,
        engine_simulation_id: comboConnectOriginal.engine_simulation_id,
        id: comboConnectOriginal.id,
        combo: true,
        combo_conecte: true,
        combo_conecte_only: isComboConnectOnly.value,
        difference: -comboConnectOriginal.installment_price,
        reducedInstallmentPrice: installmentWithComboReducedInstallmentPriceConnect,
        installments_value: Math.abs(comboConnectOriginal.installment_price),
        monthly_interest_rate: comboConnectOriginal.monthly_interest_rate || 0,
        cet: comboConnectOriginal.cet || 0,
        is_from_suggested: false,
        value: projectValue,
        registration_fee: 0,
        min_value: 0,
        max_value: 0,
        down_payment: projectDownPayment,
        interest_rate: 0,
        installments: selectedInstallmentsValue,
        indexer_kind: '',
        grace_period: 0,
        correction_name: '',
        correction_period: '',
        installments_quantity: selectedInstallmentsValue,
        iof: 0,
        identifier: '',
      }
    }
  }

  const installmentsParsed: InstallmentsWithBenefits = {
    with_benefit_combo: comboAddonOriginal && mappedProposalWithComboAddon ? [mappedProposalWithComboAddon] : [],
    with_benefit_combo_conecte: comboConnectEnabledValue.value || (comboConnectOriginal && mappedProposalWithComboConecte)
      ? mappedProposalWithComboConecte ? [mappedProposalWithComboConecte] : []
      : [],
    with_benefit_combo_comission: mappedProposalWithComboComissionAddon ? [mappedProposalWithComboComissionAddon] : [],
    without_benefit: mappedProposal && !isProposalDisabled ? [mappedProposal] : [],
  }

  return installmentsParsed
}

const installments = ref<any | []>(installmentListAdapter(props.installmentList))
const installmentWithComboId = ref<string | undefined>(installments.value?.with_benefit_combo[0]?.id)
const installmentWithComboComissionId = ref<string | undefined>(installments.value?.with_benefit_combo_comission[0]?.id)
const installmentWithComboConecteId = ref<string | undefined>(installments.value?.with_benefit_combo_conecte[0]?.id)
const preSelectedInstallmentId = ref<string | undefined>()

const withComboSelected = ref()
const withComboComissionSelected = ref()
const withComboConecteSelected = ref()
const withoutComboSelected = ref()

const pointsCombo = ref<AddonOpportunity>()
const responseSimulationOpportunities = ref<SimulationOpportunities>()

watch(() => props.installmentList, () => {
  installments.value = installmentListAdapter(props.installmentList)
  installmentWithComboId.value = installments.value?.with_benefit_combo[0]?.id
  installmentWithComboComissionId.value = installments.value?.with_benefit_combo_comission[0]?.id
  installmentWithComboConecteId.value = installments.value?.with_benefit_combo_conecte[0]?.id
  setPreSelectedInstallment()
}, { immediate: true })

function selectWithoutBenefit() {
  const defaultSelected = toRaw(installments.value?.without_benefit?.[0])
  if (!defaultSelected)
    return

  props.setSelected(defaultSelected)
  preSelectedInstallmentId.value = defaultSelected.id
  withoutComboSelected.value = { id: preSelectedInstallmentId.value ?? null }
}

function setPreSelectedInstallment() {
  const isComboDisabled = props.project?.has_combo_enabled === false
  if (!props.preSelectedInstallment) {
    if (isComboDisabled) {
      selectWithoutBenefit()
      return
    }
    else {
      if (installments.value?.with_benefit_combo_conecte?.length > 0) {
        const defaultSelected = toRaw(installments.value?.with_benefit_combo_conecte[0])
        props.setSelected(defaultSelected)
        preSelectedInstallmentId.value = installments.value?.with_benefit_combo_conecte[0].id
        withComboConecteSelected.value = { id: installmentWithComboConecteId.value ?? null }
        return
      }

      if (installments.value?.with_benefit_combo?.length > 0) {
        const defaultSelected = toRaw(installments.value?.with_benefit_combo[0])
        props.setSelected(defaultSelected)
        preSelectedInstallmentId.value = installments.value?.with_benefit_combo[0].id
        withComboSelected.value = { id: installmentWithComboId.value ?? null }
        return
      }

      selectWithoutBenefit()
      return
    }
  }

  if (props.preSelectedInstallment === 'with_combo' && installments.value?.with_benefit_combo?.length > 0) {
    const defaultSelected = toRaw(installments.value?.with_benefit_combo[0])
    props.setSelected(defaultSelected)

    preSelectedInstallmentId.value = installments.value?.with_benefit_combo[0].id

    withComboSelected.value = { id: installmentWithComboId.value ?? null }

    return
  }

  if (props.preSelectedInstallment === 'with_combo_comission' && installments.value?.with_benefit_combo_comission?.length > 0) {
    const defaultSelected = toRaw(installments.value?.with_benefit_combo_comission[0])
    props.setSelected(defaultSelected)

    preSelectedInstallmentId.value = installments.value?.with_benefit_combo_comission[0].id

    withComboComissionSelected.value = { id: installmentWithComboComissionId.value ?? null }

    return
  }

  if (props.preSelectedInstallment === 'with_combo_conecte' && installments.value && installments.value?.with_benefit_combo_conecte?.length > 0) {
    const defaultSelected = toRaw(installments.value?.with_benefit_combo_conecte[0])
    props.setSelected(defaultSelected)

    preSelectedInstallmentId.value = installments.value?.with_benefit_combo_conecte[0].id

    withComboConecteSelected.value = { id: installmentWithComboConecteId.value ?? null }

    return
  }

  selectWithoutBenefit()
}

onBeforeMount(async () => {
  await simulationOpportunities()
})

async function simulationOpportunities() {
  responseSimulationOpportunities.value = getSimulationOpportunities()

  if (responseSimulationOpportunities.value) {
    pointsCombo.value = responseSimulationOpportunities.value.addons_opportunities.find((opportunity: AddonOpportunity) => {
      return opportunity.addon_type === 'combo'
    })
  }
}

onMounted(() => {
  setEnableSelectInstallmentCombo()
})

function setEnableSelectInstallmentCombo() {
  enableSelectInstallmentCombo.value = enableSelectInstallmentComboValue.value
}

function handleSelection(value: any, type: string) {
  withComboSelected.value = null
  withComboComissionSelected.value = null
  withComboConecteSelected.value = null
  withoutComboSelected.value = null

  if (type === 'combo_connect') {
    withComboConecteSelected.value = { id: value.id }
  }
  else if (type === 'combo') {
    withComboSelected.value = { id: value.id }
  }
  else if (type === 'combo_comission') {
    withComboComissionSelected.value = { id: value.id }
  }
  else if (type === 'without_combo') {
    withoutComboSelected.value = { id: value.id }
  }

  props.setSelected(value, true)
}

const enableCombo = computed(() => {
  return props.project?.has_combo_enabled !== false
})

const totalCards = computed(() => {
  let count = 0
  if (comboConnectEnabledValue.value || installments.value?.with_benefit_combo_conecte?.length > 0)
    count++
  if (enableCombo.value)
    count++
  if (showComboCommissionInstallmentValue.value && props.installmentList?.suggested_simulation)
    count++
  count++
  return count
})

function openComboConnectRulesModal(tab: 'benefits' | 'inverters' = 'benefits') {
  emit('openComboConnectModal', tab)
}
</script>

<template>
  <div v-if="props.installmentList">
    <template v-if="enableCombo && !isComboConnectOnly">
      <a href="https://loja.solfacil.com.br/" target="_blank" title="Click para conhecer a nossa loja" class="benefits-container py-3">
        <div class="benefits-details">
          <img src="/images/combo_percent_new.svg" class="icon-cf" alt="">
          <div class="text-neutral-high-pure w-full flex md:site:flex-row flex-col md:site:items-center gap-2">
            <p v-if="!comboRegionalOfferValue" class="text-[14px] leading-4">
              {{ $t('installments.banner.start') }} <strong>{{ $t('installments.banner.middle') }}</strong> {{ $t('installments.banner.end') }}
            </p>

            <p v-if="comboRegionalOfferValue" class="text-[14px] leading-4">
              <strong>{{ $t('installments.banner_combo_offer.start') }}</strong> {{ $t('installments.banner_combo_offer.middle1') }} <strong>{{ $t('installments.banner_combo_offer.middle2') }}</strong> {{ $t('installments.banner_combo_offer.end') }}
            </p>

            <SolButton
              id="benefits-banner-button"
              variant="primary"
              size="small"
              class="text-brand-secondary-dark min-w-[134px]"
            >
              {{ $t('installments.banner.link.text') }}
              <template #icon:right><IconOpenInNew /></template>
            </SolButton>
          </div>
        </div>
      </a>
    </template>

    <div
      class="installment-cards-wrapper"
      :class="{
        'cards-single': totalCards === 1,
        'cards-two': totalCards === 2,
        'cards-three-plus': totalCards >= 3,
      }"
    >
      <template v-if="comboConnectEnabledValue || installments.with_benefit_combo_conecte?.length > 0">
        <InstallmentCard
          v-for="item in installments.with_benefit_combo_conecte"
          :key="item.id"
          :disabled="disableFields || isProjectDisabled || loadingAddons"
          :economy="formatMoney(item.reducedInstallmentPrice)"
          :hide-selection="item.combo_conecte_only"
          :has-link="true"
          link-text="Consulte regras aplicáveis"
          points="Ganhe 150.000 pontos"
          :price="formatMoney(item.installments_value)"
          :selected="withComboConecteSelected?.id === item.id"
          :show-best-offer="!item.combo_conecte_only"
          store-info="Somente na Loja Solfácil"
          title="Combo Conecte"
          @select="handleSelection(item, 'combo_connect')"
          @link-click="openComboConnectRulesModal"
          @conecte-modal-click="openComboConnectRulesModal"
        />
      </template>

      <template v-if="enableCombo">
        <InstallmentCard
          v-for="item in installments.with_benefit_combo"
          :key="item.id"
          :disabled="disableFields || isProjectDisabled || loadingAddons"
          :economy="formatMoney(item.reducedInstallmentPrice)"
          :has-link="false"
          points="Ganhe 150.000 pontos"
          :price="formatMoney(item.installments_value)"
          :selected="withComboSelected?.id === item.id"
          store-info="Somente na Loja Solfácil"
          title="Combo Fácil"
          @select="handleSelection(item, 'combo')"
        />
      </template>

      <template v-if="showComboCommissionInstallmentValue && props.installmentList.suggested_simulation">
        <InstallmentCard
          v-for="item in installments.with_benefit_combo_comission"
          :key="item.id"
          :disabled="disableFields || isProjectDisabled || loadingAddons"
          :economy="formatMoney(item.reducedInstallmentPrice)"
          :has-link="false"
          :points="pointsCombo?.gained_points && responseSimulationOpportunities?.financing_gained_points
            ? `+${(responseSimulationOpportunities.financing_gained_points + pointsCombo.gained_points).toLocaleString('pt-BR')} pontos`
            : 'Ganhe 150.000 pontos'"
          :price="formatMoney(item.installments_value)"
          :selected="withComboComissionSelected?.id === item.id"
          store-info="Somente na Loja Solfácil"
          :title="$t('installments.with_combo_comission')"
          @select="handleSelection(item, 'combo_comission')"
        />
      </template>

      <InstallmentCard
        v-for="item in installments.without_benefit"
        :key="item.id"
        :disabled="disableFields || isProjectDisabled || loadingAddons || enableSelectInstallmentCombo"
        :economy="formatMoney(0)"
        :has-link="true"
        :link-text="$t('simulation.suppliers_modal.link_installment_card')"
        points="Ganhe 30.000 pontos"
        :price="formatMoney(item.installments_value)"
        :selected="withoutComboSelected?.id === item.id"
        store-info="Em qualquer loja"
        title="Parcelamento Comum"
        @link-click="emit('openSuppliersModal')"
        @select="handleSelection(item, 'without_combo')"
      />
    </div>
  </div>

  <div v-else-if="hasDefaultInstallment && !props.showEmptyStateNotInstallments" class="empty-installment">
    <img src="/images/not-found.png" class="not-found mb-4xs">
    <h1 class="text-brand-primary-dark fonts-heading-h2 mb-4xs">
      Não foi possível exibir os dados
    </h1>
    <p class="fonts-body-x-large-regular text-brand-primary-medium mb-4xs">
      No momento estamos com instabilidade no sistema. Por favor tente novamente em alguns minutos.
    </p>
    <SolButton
      id="empty"
      type="submit"
      size="large"
      variant="secondary"
      @click="refetchInstallment"
    >
      Tentar novamente
    </SolButton>
  </div>

  <SolEmptyState
    v-else-if="hasDefaultInstallment && props.showEmptyStateNotInstallments"
    id="just-test"
    title="Sem parcelas disponíveis"
    subtitle="Não há parcelas para essa condição. Altere o prazo e veja outras opções de parcelas disponíveis."
    variant="list"
  />
</template>

<style lang="scss" scoped>
.empty-installment {
  @apply flex flex-col justify-center text-center items-center;
}

.benefits-container {
  @apply w-full my-6 grid items-center rounded-lg bg-brand-secondary-pure;

  .benefits-details {
    @apply flex items-center px-micro;

    @screen md:site {
      @apply col-span-2 px-2xs;
    }
  }
}

.icon-cf {
  @apply ml-0 mr-nano;

  @screen md:site {
    @apply mr-4xs;
  }

  @apply text-md;
}

.installment-cards-wrapper {
  @apply flex flex-nowrap gap-2 my-2xs py-4xs;
  @apply overflow-x-auto overflow-y-visible;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.cards-single {
    justify-content: center;
  }

  &.cards-two {
    justify-content: center;
  }

  &.cards-three-plus {
    justify-content: flex-start;
  }

  :deep(.installment-card) {
    @apply min-w-[188px] w-full max-w-[390px] flex-1 min-h-full;
  }
}

.disabled {
  @apply pointer-events-none opacity-75;
}
</style>
